import { useLogin, useRegister } from '@/api/iam';
import { useRefresh } from '@/api/iam/mutations/useRefresh';
import { useUserAccount } from '@/api/user';
import { AuthStep } from '@/enums/auth/authStep';
import { ModalsKey } from '@/enums/modalsKey';
import { useSystemConfiguration } from '@/hooks/theme/useSystemConfiguration';
import { useLoginCallback } from '@/hooks/useLogInCallback';
import { AuthLoginRequestDto } from '@/services/dto/auth/auth-login-request.dto';
import { useKycMeByOriginId } from '@starsoft/common/api';
import { CreateKycRequestDto } from '@starsoft/common/dto';
import {
  KycCheckpointId,
  KycIdentityOriginId,
  KYCStatus,
} from '@starsoft/common/enums';
import {
  useCurrentModal,
  useDebounce,
  useKycFormSubmission,
  useKycNavigation,
  useNotificationService,
} from '@starsoft/common/hooks';
import { useUserIdentityValidateByOriginId } from '@starsoft/common/hooks/user-identity/useUserIdentityValidateByOriginId';
import { Either } from '@starsoft/common/interfaces';
import { useEffect } from 'react';
import { Path } from 'react-hook-form';
import useAuthSearchParams from '../useAuthSearchParams';
import { UseAuthFormProps } from './props';

export default function useAuthForm<T>({ methods }: UseAuthFormProps<T>) {
  const { step } = useAuthSearchParams();
  const { refreshTokensAsync } = useRefresh();
  const loginCallback = useLoginCallback();
  const modal: Either<ModalsKey, undefined> = useCurrentModal(
    ModalsKey,
  ) as Either<ModalsKey, undefined>;

  const { watch, setValue } = methods ?? {};
  const NotificationsService = useNotificationService();

  const { isPartialAuthorized } = useUserAccount();
  const { originId } = useSystemConfiguration();
  const kycMeByOriginIdQuery = useKycMeByOriginId({
    enabled:
      modal === ModalsKey.Login && isPartialAuthorized && step === AuthStep.Kyc,
  });
  const { kyc } = kycMeByOriginIdQuery;

  const {
    handleSetKycAndStepSearchParams,
    kycCheckpointIdSearch: kycCheckpointId,
  } = useKycNavigation({
    externalEnabled:
      modal === ModalsKey.Login && isPartialAuthorized && step === AuthStep.Kyc,
  });
  const { handleKycSubmission, isLoadingKycSubmission } =
    useKycFormSubmission<T>({
      kyc,
      methods,
      externalOriginId: originId,
      enabled:
        modal === ModalsKey.Login &&
        step === AuthStep.Kyc &&
        kycCheckpointId?.toString() ===
          KycCheckpointId.VerifiedEmail?.toString(),
    });

  const isFormStep = step === AuthStep.Form;

  const useLoginMutation = useLogin();
  const useRegisterMutation = useRegister();
  const { mutate: login, requires2fa } = useLoginMutation;
  const { mutate: register } = useRegisterMutation;

  //user validation by origin
  const identifier = watch('identity.identifier' as Path<T>);
  const birthDate = watch('identity.birthDate' as Path<T>);
  const name = watch('identity.name' as Path<T>);
  const formVoucher = watch('voucher' as Path<T>);

  const debouncedVoucher = useDebounce<string>(String(formVoucher) ?? '', 500);

  // const useReferralVoucherByCodeQuery = useReferralVoucherByCode({
  //   refetchOnMount: 'always',
  //   voucher: debouncedVoucher,
  //   enabled:
  //     !!debouncedVoucher &&
  //     debouncedVoucher?.length >= 21 &&
  //     modal === ModalsKey.Register &&
  //     step === AuthStep.Form,
  // });
  // const { voucher: voucherData } = useReferralVoucherByCodeQuery;

  const debouncedIdentifier = useDebounce(identifier);
  const debouncedName = useDebounce(name);
  const debouncedBirthDate = useDebounce(birthDate);

  const payload = {
    identifier: String(debouncedIdentifier),
    name: debouncedName ? String(debouncedName) : undefined,
    birthDate: birthDate ? String(debouncedBirthDate) : undefined,
    originId: originId?.toString(),
  };

  const useUserIdentityValidateByOriginIdQuery =
    useUserIdentityValidateByOriginId({
      payload,
      originId: originId?.toString(),
      enabled: step == AuthStep.Kyc && !!identifier,
    });

  //form validation
  const email = watch('email' as Path<T>);
  const password = watch('password' as Path<T>);
  const emailPattern: RegExp =
    /^(?!.*\s)[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const isValidEmail = emailPattern.test(String(email));
  const isPasswordEmpty = String(password)?.length === 0;
  const isValidPasswordLength =
    String(password)?.length >= 4 && String(password)?.length <= 128;
  const isValidPassword = isValidPasswordLength && !isPasswordEmpty;

  const isInvalidForm =
    !isValidEmail ||
    !isValidPassword ||
    (originId?.toString() === KycIdentityOriginId.Brazil?.toString() &&
    step === AuthStep.Form &&
    modal === ModalsKey.Register
      ? !identifier
      : false);

  const onSubmit = async ({
    email,
    password,
    ...data
  }: AuthLoginRequestDto & CreateKycRequestDto) => {
    const invalid: boolean =
      email?.trim?.().length == 0 || password?.trim?.().length == 0;

    if (invalid) {
      NotificationsService.error({
        title: 'form_error_title',
        message: 'form_error_description',
      });
      return;
    }

    switch (step) {
      case AuthStep.Form:
        switch (modal) {
          case ModalsKey.Register:
            register({
              email,
              password,
              voucher: debouncedVoucher,
              identityId:
                originId?.toString() === KycIdentityOriginId.Brazil?.toString()
                  ? data?.identityId
                  : undefined,
            });
            break;
          case ModalsKey.Login:
            login({ email, password, code: data?.code });
            break;
        }
        break;
      case AuthStep.Kyc:
        const kycResponse = await handleKycSubmission(
          data as CreateKycRequestDto,
        );

        if (kycResponse?.status === KYCStatus.Verified) {
          const tokens = await refreshTokensAsync();
          loginCallback(tokens);
          return;
        }

        if (kycResponse?.stage?.checkpoint?.id) {
          handleSetKycAndStepSearchParams(
            kycResponse?.stage?.checkpoint?.id,
            AuthStep.Kyc,
          );
        }
        break;
    }
  };

  const onError = () => {
    NotificationsService.error({
      title: 'form_error_title',
      message: 'form_error_description',
    });
  };

  function handleResetOtpCode() {
    if (!requires2fa) {
      setValue('code' as Path<T>, undefined, { shouldDirty: false });
    }
  }

  useEffect(handleResetOtpCode, [requires2fa, setValue]);

  return {
    isInvalidForm,
    isValidEmail,
    isValidPassword,
    email,
    isFormStep,
    step,
    useLoginMutation: {
      ...useLoginMutation,
      isLoading: isLoadingKycSubmission || useLoginMutation?.isLoading,
    },
    useUserIdentityValidateByOriginIdQuery,
    onSubmit,
    onError,
    kycMeByOriginIdQuery,
    debouncedVoucher,
    useRegisterMutation,
    kycCheckpointId,
  };
}
